import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FarmLocationPicker from './FarmLocationPicker';
import WeatherSettings from './WeatherSettings';
import SpraySettings from './SpraySettings';
import OrchardManager from './OrchardManager';
import PestManager from './PestManager';
import ScheduledReportManager from './ScheduledReportManager';
import RoleManagement from './RoleManagement';
import UserManagement from './UserManagement';
import LanguageSwitcher from './LanguageSwitcher';
import DateFormatSelector from './DateFormatSelector';
import { getSettings, updateSettings } from '../services/settingsService';
import { getTenantInfo, uploadLogo, updateTenantInfo } from '../services/tenantService';
import { usePwaUpdate } from '../contexts/PwaUpdateContext';
import { resetSyncState } from '../services/syncService';
import { updateDashboardSettings } from '../services/dashboardSettingsService';
import { useModuleNav } from '../contexts/ModuleNavContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { useMenu } from '../auth/useMenu';
import SyncLogTable from './SyncLogTable';
import PageHeader from './shell/PageHeader';
import { Field, FormSection, Notice, TabBar, TabPanel } from './patterns';
import { runningBuildHash } from '../utils/buildVersion';
import './Settings.css';

const COMMON_TIMEZONES = [
  'Africa/Johannesburg',
  'Africa/Nairobi',
  'Africa/Cairo',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC'
];

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { updateAvailable, updateApp } = usePwaUpdate();
  const { setModule } = useModuleNav();
  const canManageTenant = useMenu('settings:tenant');

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('settings.title') },
  ]);

  useEffect(() => {
    setModule(t('nav.settings'), []);
    return () => setModule(null, null);
  }, [setModule, t]);
  const [retentionDays, setRetentionDays] = useState<number>(7);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [farmTimezone, setFarmTimezone] = useState<string>('Africa/Johannesburg');
  const [centralLocation, setCentralLocation] = useState<{ type: 'Point'; coordinates: [number, number] } | null>(null);
  const [locationSavedMessage, setLocationSavedMessage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  // Scouting Freshness Thresholds state
  const [freshnessGreenDays, setFreshnessGreenDays] = useState<number>(3);
  const [freshnessYellowDays, setFreshnessYellowDays] = useState<number>(7);
  const [freshnessGreenColor, setFreshnessGreenColor] = useState<string>('#27ae60');
  const [freshnessYellowColor, setFreshnessYellowColor] = useState<string>('#f39c12');
  const [freshnessRedColor, setFreshnessRedColor] = useState<string>('#e74c3c');
  const [thresholdsSaving, setThresholdsSaving] = useState(false);
  const [thresholdsSavedMessage, setThresholdsSavedMessage] = useState<string | null>(null);

  const settingsBase = location.pathname.startsWith('/settings/tenant')
    ? '/settings/tenant'
    : '/settings/me';

  const getActiveTab = () => {
    if (!canManageTenant) return 'general';
    const path = location.pathname;
    if (path.includes('/pests')) return 'pest';
    if (path.includes('/general')) return 'general';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/roles')) return 'roles';
    if (path.includes('/users')) return 'users';
    if (path.includes('/sync-logs')) return 'sync-logs';
    if (path.includes('/weather')) return 'weather';
    if (path.includes('/spray')) return 'spray';
    return 'orchard';
  };

  const handleTabSelect = (k: string) => {
    if (k === 'pest') navigate(`${settingsBase}/pests`);
    else if (k === 'general') navigate(`${settingsBase}/general`);
    else if (k === 'reports') navigate(`${settingsBase}/reports`);
    else if (k === 'roles') navigate(`${settingsBase}/roles`);
    else if (k === 'users') navigate(`${settingsBase}/users`);
    else if (k === 'sync-logs') navigate(`${settingsBase}/sync-logs`);
    else if (k === 'weather') navigate(`${settingsBase}/weather`);
    else if (k === 'spray') navigate(`${settingsBase}/spray`);
    else navigate(`${settingsBase}/orchards`);
  };

  useEffect(() => {
    getSettings().then(settings => {
      setRetentionDays(settings.retentionDays);
    });
    getTenantInfo().then(tenant => {
      setLogoUrl(tenant.logoUrl);
      if (tenant.farmTimezone) {
        setFarmTimezone(tenant.farmTimezone);
      }
      if (tenant.centralLocation && Array.isArray(tenant.centralLocation.coordinates)) {
        setCentralLocation({
          type: 'Point',
          coordinates: [tenant.centralLocation.coordinates[0], tenant.centralLocation.coordinates[1]]
        });
      }
      const t = tenant.scoutingFreshnessThresholds;
      if (t) {
        if (t.greenDays  != null) setFreshnessGreenDays(t.greenDays);
        if (t.yellowDays != null) setFreshnessYellowDays(t.yellowDays);
        if (t.greenColor)  setFreshnessGreenColor(t.greenColor);
        if (t.yellowColor) setFreshnessYellowColor(t.yellowColor);
        if (t.redColor)    setFreshnessRedColor(t.redColor);
      }
    }).catch(err => {
      console.error('Failed to fetch tenant info:', err);
    });
  }, []);

  const handleRetentionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    if (rawValue === '') {
      setRetentionDays('' as any); // Allow clearing for editing
      return;
    }
    const value = parseInt(rawValue, 10);
    if (!isNaN(value) && value >= 0 && value <= 30) {
      setRetentionDays(value);
      updateSettings({ retentionDays: value });
    }
  };

  const handleSaveFarmLocation = async (location: { type: 'Point'; coordinates: [number, number] }) => {
    setLocationSavedMessage(null);
    try {
      await updateTenantInfo({ centralLocation: location });
      setCentralLocation(location);
      setLocationSavedMessage('Farm location saved successfully.');
    } catch (err) {
      setLocationSavedMessage('Failed to save farm location. Please try again.');
      throw err;
    }
  };

  const handleTimezoneChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFarmTimezone(value);
    try {
      await updateTenantInfo({ farmTimezone: value });
    } catch (err) {
      console.error('Failed to update farm timezone:', err);
      setError('Failed to update farm timezone.');
    }
  };

  const handleSaveThresholds = async () => {
    setThresholdsSaving(true);
    setThresholdsSavedMessage(null);
    try {
      const thresholds = {
        greenDays: freshnessGreenDays,
        yellowDays: freshnessYellowDays,
        greenColor: freshnessGreenColor,
        yellowColor: freshnessYellowColor,
        redColor: freshnessRedColor,
      };
      await updateTenantInfo({ scoutingFreshnessThresholds: thresholds });
      await updateDashboardSettings(thresholds);
      setThresholdsSavedMessage('Thresholds saved successfully.');
    } catch (err) {
      setThresholdsSavedMessage('Failed to save thresholds. Please try again.');
    } finally {
      setThresholdsSaving(false);
    }
  };

  const handleResetSyncState = async () => {
    setResetMessage(null);
    try {
      const count = await resetSyncState();
      setResetMessage(`Reset complete. ${count} session(s) have been reset to pending.`);
    } catch (err) {
      setResetMessage('Failed to reset sync state. Please try again.');
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      const tenant = await uploadLogo(file);
      setLogoUrl(tenant.logoUrl);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="settings-page">
      <PageHeader title={t('settings.title')} />
      <div className="settings-page__body">
        <TabBar activeKey={getActiveTab()} onSelect={handleTabSelect}>
          {canManageTenant && (
            <TabPanel id="orchard" title={t('settings.tabs.orchardSetup')}>
              <OrchardManager basePath={`${settingsBase}/orchards`} />
            </TabPanel>
          )}
          {canManageTenant && (
            <TabPanel id="pest" title={t('settings.tabs.pestSetup')}>
              <PestManager />
            </TabPanel>
          )}
          {canManageTenant && (
            <TabPanel id="reports" title={t('settings.tabs.scheduledReports')}>
              <ScheduledReportManager />
            </TabPanel>
          )}
          {canManageTenant && (
            <TabPanel id="roles" title={t('settings.tabs.roles')}>
              <RoleManagement />
            </TabPanel>
          )}
          {canManageTenant && (
            <TabPanel id="users" title={t('settings.tabs.users')}>
              <UserManagement />
            </TabPanel>
          )}
          {canManageTenant && (
            <TabPanel id="sync-logs" title={t('settings.tabs.syncLogs')}>
              <SyncLogTable />
            </TabPanel>
          )}
          {canManageTenant && (
            <TabPanel id="weather" title={t('settings.tabs.weather')}>
              <WeatherSettings />
            </TabPanel>
          )}
          {canManageTenant && (
            <TabPanel id="spray" title={t('settings.tabs.spray')}>
              <SpraySettings />
            </TabPanel>
          )}
          <TabPanel id="general" title={t('settings.tabs.generalSetup')}>
            <div className="settings-general">
              {updateAvailable && (
                <Notice tone="warn" action={<button type="button" className="pat-btn pat-btn--primary" onClick={updateApp}>Update Now</button>}>
                  <span>
                    <i className="bi bi-arrow-clockwise" aria-hidden="true" /> A new app version is ready to install.
                  </span>
                </Notice>
              )}

              <p className="text-muted" data-testid="settings-build-version">
                {t('settings.general.buildVersion', {
                  hash: runningBuildHash() ?? t('settings.general.buildVersionUnknown'),
                })}
              </p>

              <FormSection
                id="settings-retention"
                title={t('settings.general.retentionDuration')}
                description={t('settings.general.retentionDescription')}
              >
                <Field
                  label={t('settings.general.retentionDuration')}
                  controlId="retentionDuration"
                >
                  <input
                    id="retentionDuration"
                    type="number"
                    min={0}
                    max={30}
                    value={retentionDays === ('' as any) ? '' : retentionDays}
                    onChange={handleRetentionChange}
                    onBlur={() => {
                      if (retentionDays === ('' as any)) {
                        setRetentionDays(7);
                        updateSettings({ retentionDays: 7 });
                      }
                    }}
                  />
                </Field>
              </FormSection>

              <FormSection
                id="settings-timezone"
                title={t('settings.general.farmTimezone')}
                description={t('settings.general.farmTimezoneDescription')}
              >
                <Field
                  label={t('settings.general.farmTimezone')}
                  controlId="farmTimezone"
                >
                  <select
                    id="farmTimezone"
                    value={farmTimezone}
                    onChange={handleTimezoneChange}
                  >
                    {COMMON_TIMEZONES.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </Field>
              </FormSection>

              <FormSection
                id="settings-farm-location"
                title={t('settings.general.farmLocation')}
              >
                <FarmLocationPicker
                  initialLocation={centralLocation}
                  onSave={handleSaveFarmLocation}
                />
                {locationSavedMessage && (
                  <Notice tone={locationSavedMessage.toLowerCase().startsWith('failed') ? 'danger' : 'success'} role="alert">
                    {locationSavedMessage}
                  </Notice>
                )}
              </FormSection>

              <FormSection
                id="settings-language"
                title={t('settings.general.language')}
                description={t('settings.general.languageDescription')}
              >
                <LanguageSwitcher />
              </FormSection>

              <FormSection
                id="settings-date-format"
                title={t('dateFormat.label')}
              >
                <DateFormatSelector />
              </FormSection>

              <FormSection
                id="settings-freshness"
                title={t('settings.general.freshnessTitle')}
                description={t('settings.general.freshnessDescription')}
              >
                <div className="settings-freshness-grid">
                  <Field
                    label={t('settings.general.freshnessGreenDays')}
                    controlId="freshnessGreenDays"
                  >
                    <input
                      id="freshnessGreenDays"
                      type="number"
                      min={0}
                      max={365}
                      value={freshnessGreenDays}
                      onChange={e => setFreshnessGreenDays(parseInt(e.target.value, 10) || 0)}
                    />
                  </Field>
                  <Field
                    label={t('settings.general.freshnessYellowDays')}
                    controlId="freshnessYellowDays"
                  >
                    <input
                      id="freshnessYellowDays"
                      type="number"
                      min={0}
                      max={365}
                      value={freshnessYellowDays}
                      onChange={e => setFreshnessYellowDays(parseInt(e.target.value, 10) || 0)}
                    />
                  </Field>
                  <Field
                    label={t('settings.general.freshnessGreenColor')}
                    controlId="freshnessGreenColor"
                  >
                    <input
                      id="freshnessGreenColor"
                      type="color"
                      value={freshnessGreenColor}
                      onChange={e => setFreshnessGreenColor(e.target.value)}
                      className="settings-color-input"
                    />
                  </Field>
                  <Field
                    label={t('settings.general.freshnessYellowColor')}
                    controlId="freshnessYellowColor"
                  >
                    <input
                      id="freshnessYellowColor"
                      type="color"
                      value={freshnessYellowColor}
                      onChange={e => setFreshnessYellowColor(e.target.value)}
                      className="settings-color-input"
                    />
                  </Field>
                  <Field
                    label={t('settings.general.freshnessRedColor')}
                    controlId="freshnessRedColor"
                  >
                    <input
                      id="freshnessRedColor"
                      type="color"
                      value={freshnessRedColor}
                      onChange={e => setFreshnessRedColor(e.target.value)}
                      className="settings-color-input"
                    />
                  </Field>
                </div>
                {thresholdsSavedMessage && (
                  <Notice tone={thresholdsSavedMessage.startsWith('Failed') || thresholdsSavedMessage.startsWith('Kon nie') ? 'danger' : 'success'} role="alert">
                    {thresholdsSavedMessage}
                  </Notice>
                )}
                <div className="settings-section-actions">
                  <button
                    type="button"
                    className="pat-btn pat-btn--primary"
                    onClick={handleSaveThresholds}
                    disabled={thresholdsSaving}
                  >
                    {thresholdsSaving ? t('settings.general.saving') : t('settings.general.saveThresholds')}
                  </button>
                </div>
              </FormSection>

              <FormSection
                id="settings-customer-logo"
                title={t('settings.general.customerLogo')}
                description={t('settings.general.uploadLogoDescription')}
              >
                <div className="settings-logo">
                  {logoUrl ? (
                    <div className="settings-logo__preview">
                      <img
                        src={logoUrl}
                        alt={t('settings.general.customerLogo')}
                        className="settings-logo__img"
                      />
                    </div>
                  ) : (
                    <Notice tone="info">
                      {t('settings.general.noLogoUploaded')}
                    </Notice>
                  )}
                </div>
                {error && (
                  <Notice tone="danger">
                    {error}
                  </Notice>
                )}
                <Field
                  label={t('settings.general.customerLogo')}
                  controlId="customerLogo"
                >
                  <input
                    id="customerLogo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                  />
                </Field>
                {uploading && (
                  <div className="settings-uploading">{t('settings.general.uploading')}</div>
                )}
              </FormSection>

              <FormSection
                id="settings-data-recovery"
                title="Local Data Recovery"
                description="If scouting sessions appear to be missing after a PWA update, use the recovery tool to inspect and restore any sessions that are stored locally on this device but were not yet synced to the server."
              >
                <div className="settings-section-actions">
                  <Link to="/data-recovery" className="pat-btn pat-btn--outline">
                    <i className="bi bi-database-exclamation" aria-hidden="true" /> Open Recovery Tool
                  </Link>
                </div>
              </FormSection>

              <FormSection
                id="settings-sync-recovery"
                title="Sync State Recovery"
                description={'If the app appears stuck in a syncing state, use this button to reset any sessions currently marked as "syncing" or "quarantined" back to "pending" so they will be retried on the next sync cycle.'}
              >
                {resetMessage && (
                  <Notice tone={resetMessage.startsWith('Failed') ? 'danger' : 'success'} role="alert">
                    {resetMessage}
                  </Notice>
                )}
                <div className="settings-section-actions">
                  <button
                    type="button"
                    className="pat-btn pat-btn--danger"
                    onClick={handleResetSyncState}
                  >
                    <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> Reset Sync State
                  </button>
                </div>
              </FormSection>
            </div>
          </TabPanel>
        </TabBar>
      </div>
    </div>
  );
};

export default Settings;
